import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/services/adminApi";
import type { SlideGenerationTrace } from "@/types/admin";
import {
    ArrowLeft,
    RefreshCcw,
    AlertTriangle,
    Star,
    ChevronDown,
    ChevronRight,
} from "lucide-react";

interface SlideFeedback {
    id: string;
    jobId: string;
    slideNumber: number | null;
    userId: string;
    rating: number;
    tags: string[];
    comment: string | null;
    createdAt: string;
}

// Phase/slide sub-shapes — the trace type currently has these typed as `any`.
interface ThemePhase {
    prompt?: string;
    llmResponse?: unknown;
    validatedTheme?: unknown;
    fontValidation?: {
        requestedHeading: string;
        requestedBody: string;
        finalHeading: string;
        finalBody: string;
    };
    durationMs?: number;
    tokens?: number;
    cost?: number;
    error?: string;
}

interface PlanningPhase {
    prompt?: string;
    rawLlmPlan?: unknown;
    validatedPlan?: unknown;
    diversityFixups?: {
        before: Array<{ n: number; archetype?: string; cardLayout?: string; hasImage: boolean }>;
        after: Array<{ n: number; archetype?: string; cardLayout?: string; hasImage: boolean }>;
    };
    durationMs?: number;
    tokens?: number;
    cost?: number;
    error?: string;
}

interface ConfirmationPhase {
    shown: boolean;
    decision?: "approve" | "modify" | "cancel" | "timeout";
    modifiedPlan?: unknown;
    feedback?: string;
    userLatencyMs?: number;
}

interface SlidePhaseEntry {
    slideNumber: number;
    plan?: any;
    contentPrompt?: string;
    toolCalls?: Array<{ name: string; input: unknown }>;
    finalComponents?: unknown;
    contentDurationMs?: number;
    heroHtmlPrompt?: string;
    heroHtmlResponse?: string;
    heroHtmlDurationMs?: number;
    imagePrompt?: string;
    imageModel?: string;
    imageUrl?: string;
    imageFallbackUsed?: boolean;
    imageDurationMs?: number;
    retryCount?: number;
    durationMs?: number;
    tokens?: number;
    cost?: number;
    error?: string;
}

// ---------- helpers ----------

function formatDuration(ms: number | null | undefined): string {
    if (ms == null) return "-";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return `${m}m ${s}s`;
}

function formatCost(usd: string | number | null | undefined): string {
    if (usd == null) return "-";
    const n = typeof usd === "string" ? Number(usd) : usd;
    if (Number.isNaN(n)) return "-";
    if (n === 0) return "$0";
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(3)}`;
}

function formatTokens(n: number | null | undefined): string {
    if (n == null) return "-";
    if (n < 1000) return String(n);
    return `${(n / 1000).toFixed(1)}k`;
}

function statusBadge(status: SlideGenerationTrace["status"]) {
    switch (status) {
        case "COMPLETED":
            return <Badge className="bg-green-100 text-green-800">completed</Badge>;
        case "IN_PROGRESS":
            return <Badge className="bg-blue-100 text-blue-800">in progress</Badge>;
        case "FAILED":
            return <Badge className="bg-red-100 text-red-800">failed</Badge>;
        case "CANCELLED":
            return <Badge className="bg-gray-200 text-gray-800">cancelled</Badge>;
        default:
            return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
}

function decisionBadge(decision?: string) {
    if (!decision) return null;
    const map: Record<string, string> = {
        approve: "bg-green-100 text-green-800",
        modify: "bg-yellow-100 text-yellow-800",
        cancel: "bg-gray-200 text-gray-800",
        timeout: "bg-orange-100 text-orange-800",
    };
    return (
        <Badge className={map[decision] || "bg-gray-100 text-gray-800"}>{decision}</Badge>
    );
}

function JsonBlock({ data, maxHeight = "max-h-96" }: { data: unknown; maxHeight?: string }) {
    if (data == null) return <p className="text-xs text-gray-400">(empty)</p>;
    let str: string;
    try {
        str = JSON.stringify(data, null, 2);
    } catch {
        str = String(data);
    }
    return (
        <pre
            className={`text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto ${maxHeight} font-mono whitespace-pre-wrap break-words`}
        >
            {str}
        </pre>
    );
}

function TextBlock({
    text,
    maxHeight = "max-h-72",
}: {
    text?: string | null;
    maxHeight?: string;
}) {
    if (!text) return <p className="text-xs text-gray-400">(empty)</p>;
    return (
        <pre
            className={`text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto ${maxHeight} font-mono whitespace-pre-wrap break-words`}
        >
            {text}
        </pre>
    );
}

function DurationBreakdown({ slide }: { slide: SlidePhaseEntry }) {
    const content = slide.contentDurationMs;
    const image = slide.imageDurationMs;
    const hero = slide.heroHtmlDurationMs;
    const hasBreakdown = content != null || image != null || hero != null;

    if (!hasBreakdown) {
        return <span>{formatDuration(slide.durationMs)}</span>;
    }

    const parts: Array<{ label: string; ms: number; color: string }> = [];
    if (content != null) parts.push({ label: "content", ms: content, color: "text-gray-700" });
    if (image != null) parts.push({ label: "image", ms: image, color: "text-blue-700" });
    if (hero != null) parts.push({ label: "hero", ms: hero, color: "text-purple-700" });

    return (
        <span
            className="flex items-center gap-1.5"
            title={`total: ${formatDuration(slide.durationMs)}`}
        >
            <span className="font-bold">{formatDuration(slide.durationMs)}</span>
            <span className="text-gray-300">·</span>
            {parts.map((p, i) => (
                <span key={p.label} className={p.color}>
                    {i > 0 && <span className="text-gray-300 mr-1">·</span>}
                    <span className="text-gray-400">{p.label}</span>{" "}
                    {formatDuration(p.ms)}
                </span>
            ))}
        </span>
    );
}

function PhaseStats({
    durationMs,
    tokens,
    cost,
}: {
    durationMs?: number;
    tokens?: number;
    cost?: number;
}) {
    return (
        <div className="flex gap-4 text-xs font-mono text-gray-600">
            <span>
                <span className="text-gray-400">duration:</span>{" "}
                <span className="font-bold">{formatDuration(durationMs)}</span>
            </span>
            <span>
                <span className="text-gray-400">tokens:</span>{" "}
                <span className="font-bold">{formatTokens(tokens)}</span>
            </span>
            <span>
                <span className="text-gray-400">cost:</span>{" "}
                <span className="font-bold">{formatCost(cost)}</span>
            </span>
        </div>
    );
}

function Section({
    title,
    defaultOpen = true,
    children,
    right,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
    right?: React.ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card>
            <div onClick={() => setOpen(!open)}>
                <CardHeader className="pb-3 cursor-pointer flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        {open ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                        {title}
                    </CardTitle>
                    {right}
                </CardHeader>
            </div>
            {open && <CardContent className="pt-0">{children}</CardContent>}
        </Card>
    );
}

function ColorSwatch({ label, color }: { label: string; color?: string }) {
    if (!color || typeof color !== "string") return null;
    return (
        <div className="flex items-center gap-2">
            <div
                className="w-6 h-6 rounded border border-gray-300"
                style={{ backgroundColor: color }}
            />
            <div className="text-xs font-mono">
                <div className="text-gray-500">{label}</div>
                <div>{color}</div>
            </div>
        </div>
    );
}

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
    const clamped = Math.max(0, Math.min(5, Math.round(rating)));
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((v) => (
                <Star
                    key={v}
                    style={{ width: size, height: size }}
                    className={
                        v <= clamped
                            ? "fill-yellow-400 text-yellow-400"
                            : "fill-transparent text-gray-300"
                    }
                />
            ))}
        </div>
    );
}

function FeedbackRow({ fb }: { fb: SlideFeedback }) {
    return (
        <div className="flex items-start gap-3 text-xs py-2 border-b border-gray-100 last:border-0">
            <StarRating rating={fb.rating} />
            <div className="flex-1 min-w-0">
                {fb.comment ? (
                    <div className="text-gray-700 whitespace-pre-wrap">{fb.comment}</div>
                ) : (
                    <div className="text-gray-400 italic">No comment</div>
                )}
            </div>
            <div className="text-[10px] text-gray-400 whitespace-nowrap">
                {new Date(fb.createdAt).toLocaleString()}
            </div>
        </div>
    );
}

// ---------- page ----------

export default function SlideTraceDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [trace, setTrace] = useState<
        (SlideGenerationTrace & { feedback?: SlideFeedback[] }) | null
    >(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAll = async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const traceRes = await adminApi.getSlideTraceById(id);
            setTrace(traceRes);
        } catch (err: any) {
            console.error("Failed to fetch slide trace:", err);
            setError(err?.response?.data?.error || err?.message || "Failed to load trace");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const feedback = useMemo<SlideFeedback[]>(
        () => (trace?.feedback as SlideFeedback[] | undefined) ?? [],
        [trace]
    );

    const deckFeedback = useMemo(
        () => feedback.filter((f) => f.slideNumber == null),
        [feedback]
    );

    const deckStats = useMemo(() => {
        if (deckFeedback.length === 0) {
            return { avg: 0, count: 0, withComment: 0 };
        }
        const sum = deckFeedback.reduce((acc, f) => acc + f.rating, 0);
        const withComment = deckFeedback.filter((f) => f.comment && f.comment.trim()).length;
        return {
            avg: sum / deckFeedback.length,
            count: deckFeedback.length,
            withComment,
        };
    }, [deckFeedback]);

    if (loading && !trace) {
        return (
            <div className="flex items-center justify-center py-16">
                <RefreshCcw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (error || !trace) {
        return (
            <div className="space-y-4">
                <Link
                    to="/slide-traces"
                    className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to slide traces
                </Link>
                <Card>
                    <CardContent className="py-10 text-center text-red-600">
                        {error || "Trace not found"}
                    </CardContent>
                </Card>
            </div>
        );
    }

    const themePhase = trace.themePhase as ThemePhase | null;
    const planningPhase = trace.planningPhase as PlanningPhase | null;
    const confirmationPhase = trace.confirmationPhase as ConfirmationPhase | null;
    const slidesPhase = (trace.slidesPhase as SlidePhaseEntry[] | null) || [];
    const input = trace.input as SlideGenerationTrace["input"] & {
        provider?: string;
        modelName?: string;
    };

    return (
        <div className="space-y-5">
            {/* Top bar */}
            <div>
                <Link
                    to="/slide-traces"
                    className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 mb-2"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to slide traces
                </Link>
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold text-gray-900 truncate">
                            {input.topic || "(no topic)"}
                        </h1>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                            {statusBadge(trace.status)}
                            <span className="text-xs font-mono text-gray-500">
                                <span className="text-gray-400">duration:</span>{" "}
                                <span className="font-bold text-gray-700">
                                    {formatDuration(trace.totalDurationMs)}
                                </span>
                            </span>
                            <span className="text-xs font-mono text-gray-500">
                                <span className="text-gray-400">cost:</span>{" "}
                                <span className="font-bold text-gray-700">
                                    {formatCost(trace.totalCostUsd)}
                                </span>
                            </span>
                            <span className="text-xs font-mono text-gray-500">
                                <span className="text-gray-400">tokens:</span>{" "}
                                <span className="font-bold text-gray-700">
                                    {formatTokens(trace.totalTokens)}
                                </span>
                            </span>
                            <span className="text-xs font-mono text-gray-500">
                                <span className="text-gray-400">slides:</span>{" "}
                                <span className="font-bold text-gray-700">
                                    {slidesPhase.length || input.slideCount}
                                </span>
                            </span>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 font-mono flex-wrap">
                            <span>
                                <span className="text-gray-400">created:</span>{" "}
                                {new Date(trace.createdAt).toLocaleString()}
                            </span>
                            {trace.completedAt && (
                                <span>
                                    <span className="text-gray-400">completed:</span>{" "}
                                    {new Date(trace.completedAt).toLocaleString()}
                                </span>
                            )}
                            <span
                                className="cursor-pointer hover:text-blue-600"
                                title="click to copy jobId"
                                onClick={() => navigator.clipboard.writeText(trace.jobId)}
                            >
                                <span className="text-gray-400">jobId:</span> {trace.jobId}
                            </span>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchAll}>
                        <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
                    </Button>
                </div>
                {trace.errorMessage && (
                    <div className="mt-3 text-xs font-mono bg-red-50 border border-red-200 text-red-700 rounded p-3 flex gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        {trace.errorMessage}
                    </div>
                )}
            </div>

            {/* User feedback — deck level */}
            <Section
                title="User feedback"
                right={
                    deckStats.count > 0 ? (
                        <div className="flex items-center gap-3 text-xs font-mono">
                            <StarRating rating={deckStats.avg} />
                            <span className="text-gray-700 font-bold">
                                {deckStats.avg.toFixed(1)} / 5
                            </span>
                            <span className="text-gray-400">
                                {deckStats.count} rating{deckStats.count === 1 ? "" : "s"}
                                {deckStats.withComment > 0 &&
                                    ` · ${deckStats.withComment} with comment`}
                            </span>
                        </div>
                    ) : null
                }
            >
                {deckFeedback.length === 0 ? (
                    <p className="text-sm text-gray-400">No feedback yet</p>
                ) : (
                    <div className="border border-gray-200 rounded p-2 bg-white">
                        {deckFeedback.map((fb) => (
                            <FeedbackRow key={fb.id} fb={fb} />
                        ))}
                    </div>
                )}
            </Section>

            {/* Input */}
            <Section title="Input">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <LabeledField label="topic" value={input.topic} />
                    <LabeledField label="slideCount" value={String(input.slideCount)} />
                    <LabeledField label="style" value={input.style} />
                    <LabeledField label="editMode" value={input.isEditMode ? "yes" : "no"} />
                    {input.provider && (
                        <LabeledField label="provider" value={input.provider} />
                    )}
                    {input.modelName && (
                        <LabeledField label="modelName" value={input.modelName} />
                    )}
                    {input.isEditMode && (
                        <>
                            {input.existingJobId && (
                                <LabeledField
                                    label="existingJobId"
                                    value={input.existingJobId}
                                />
                            )}
                            {input.slidesToEdit && (
                                <LabeledField
                                    label="slidesToEdit"
                                    value={input.slidesToEdit.join(", ")}
                                />
                            )}
                            {input.editInstructions && (
                                <LabeledField
                                    label="editInstructions"
                                    value={input.editInstructions}
                                    wide
                                />
                            )}
                        </>
                    )}
                    {input.additionalInstructions && (
                        <LabeledField
                            label="additionalInstructions"
                            value={input.additionalInstructions}
                            wide
                        />
                    )}
                    {input.context && (
                        <div className="col-span-full">
                            <div className="text-xs text-gray-500 mb-1">context</div>
                            <TextBlock text={input.context} maxHeight="max-h-60" />
                        </div>
                    )}
                </div>
            </Section>

            {/* Theme phase */}
            {themePhase && (
                <Section
                    title="Theme phase"
                    right={
                        <PhaseStats
                            durationMs={themePhase.durationMs}
                            tokens={themePhase.tokens}
                            cost={themePhase.cost}
                        />
                    }
                >
                    {themePhase.error && (
                        <div className="mb-3 text-xs font-mono bg-red-50 border border-red-200 text-red-700 rounded p-2">
                            {themePhase.error}
                        </div>
                    )}
                    {themePhase.fontValidation && (
                        <FontValidationCard fv={themePhase.fontValidation} />
                    )}
                    {themePhase.validatedTheme != null && (
                        <ThemeSwatches theme={themePhase.validatedTheme} />
                    )}
                    <div className="space-y-3 mt-3">
                        {themePhase.prompt != null && (
                            <Labeled title="prompt">
                                <TextBlock text={themePhase.prompt} />
                            </Labeled>
                        )}
                        {themePhase.llmResponse != null && (
                            <Labeled title="llmResponse">
                                <JsonBlock data={themePhase.llmResponse} />
                            </Labeled>
                        )}
                        {themePhase.validatedTheme != null && (
                            <Labeled title="validatedTheme">
                                <JsonBlock data={themePhase.validatedTheme} />
                            </Labeled>
                        )}
                    </div>
                </Section>
            )}

            {/* Planning phase */}
            {planningPhase && (
                <Section
                    title="Planning phase"
                    right={
                        <PhaseStats
                            durationMs={planningPhase.durationMs}
                            tokens={planningPhase.tokens}
                            cost={planningPhase.cost}
                        />
                    }
                >
                    {planningPhase.error && (
                        <div className="mb-3 text-xs font-mono bg-red-50 border border-red-200 text-red-700 rounded p-2">
                            {planningPhase.error}
                        </div>
                    )}
                    {planningPhase.diversityFixups && (
                        <DiversityFixupsCard fixups={planningPhase.diversityFixups} />
                    )}
                    <div className="space-y-3 mt-3">
                        {planningPhase.prompt != null && (
                            <Labeled title="prompt">
                                <TextBlock text={planningPhase.prompt} />
                            </Labeled>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {planningPhase.rawLlmPlan != null && (
                                <Labeled title="rawLlmPlan">
                                    <JsonBlock data={planningPhase.rawLlmPlan} />
                                </Labeled>
                            )}
                            {planningPhase.validatedPlan != null && (
                                <Labeled title="validatedPlan">
                                    <JsonBlock data={planningPhase.validatedPlan} />
                                </Labeled>
                            )}
                        </div>
                    </div>
                </Section>
            )}

            {/* Confirmation phase */}
            {confirmationPhase && (
                <Section title="Confirmation phase">
                    <div className="flex items-center gap-3 flex-wrap mb-3">
                        <Badge
                            className={
                                confirmationPhase.shown
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-gray-100 text-gray-700"
                            }
                        >
                            {confirmationPhase.shown ? "shown" : "not shown"}
                        </Badge>
                        {decisionBadge(confirmationPhase.decision)}
                        {confirmationPhase.userLatencyMs != null && (
                            <span className="text-xs font-mono text-gray-600">
                                <span className="text-gray-400">userLatency:</span>{" "}
                                {formatDuration(confirmationPhase.userLatencyMs)}
                            </span>
                        )}
                    </div>
                    {confirmationPhase.feedback && (
                        <Labeled title="feedback">
                            <TextBlock
                                text={confirmationPhase.feedback}
                                maxHeight="max-h-40"
                            />
                        </Labeled>
                    )}
                    {confirmationPhase.modifiedPlan != null && (
                        <div className="mt-3">
                            <Labeled title="modifiedPlan">
                                <JsonBlock data={confirmationPhase.modifiedPlan} />
                            </Labeled>
                        </div>
                    )}
                </Section>
            )}

            {/* Slides phase */}
            {slidesPhase.length > 0 && (
                <Section title={`Slides (${slidesPhase.length})`}>
                    <div className="space-y-3">
                        {slidesPhase
                            .slice()
                            .sort((a, b) => a.slideNumber - b.slideNumber)
                            .map((slide) => (
                                <SlideCard key={slide.slideNumber} slide={slide} />
                            ))}
                    </div>
                </Section>
            )}
        </div>
    );
}

// ---------- smaller building blocks ----------

function Labeled({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <div className="text-xs text-gray-500 mb-1 font-semibold">{title}</div>
            {children}
        </div>
    );
}

function LabeledField({
    label,
    value,
    wide = false,
}: {
    label: string;
    value: string;
    wide?: boolean;
}) {
    return (
        <div className={wide ? "col-span-full" : ""}>
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-sm text-gray-800 break-words">{value}</div>
        </div>
    );
}

function FontValidationCard({ fv }: { fv: NonNullable<ThemePhase["fontValidation"]> }) {
    const headingFallback = fv.requestedHeading !== fv.finalHeading;
    const bodyFallback = fv.requestedBody !== fv.finalBody;
    const row = (label: string, requested: string, final: string, fallback: boolean) => (
        <div
            className={`flex items-center gap-3 text-xs font-mono p-2 rounded ${
                fallback ? "bg-amber-50 border border-amber-200" : "bg-gray-50"
            }`}
        >
            <span className="text-gray-500 w-16">{label}</span>
            <span className={fallback ? "text-gray-500 line-through" : ""}>{requested}</span>
            <span className="text-gray-400">→</span>
            <span className={fallback ? "text-amber-700 font-bold" : ""}>{final}</span>
            {fallback && (
                <Badge className="bg-amber-100 text-amber-800 text-[10px] ml-auto">
                    silent fallback
                </Badge>
            )}
        </div>
    );
    return (
        <div className="space-y-2 mb-3">
            <div className="text-xs font-semibold text-gray-600">font validation</div>
            {row("heading", fv.requestedHeading, fv.finalHeading, headingFallback)}
            {row("body", fv.requestedBody, fv.finalBody, bodyFallback)}
        </div>
    );
}

function ThemeSwatches({ theme }: { theme: unknown }) {
    if (!theme || typeof theme !== "object") return null;
    const t = theme as Record<string, unknown>;
    const colors = (t.colors || {}) as Record<string, unknown>;
    const accent = (t.accentPrimary || colors.accentPrimary) as string | undefined;
    const surface = (t.surface || colors.surface) as string | undefined;
    const textPrimary = (t.textPrimary || colors.textPrimary) as string | undefined;
    if (!accent && !surface && !textPrimary) return null;
    return (
        <div className="flex items-center gap-4 mb-3 flex-wrap">
            <ColorSwatch label="accentPrimary" color={accent} />
            <ColorSwatch label="surface" color={surface} />
            <ColorSwatch label="textPrimary" color={textPrimary} />
        </div>
    );
}

function DiversityFixupsCard({
    fixups,
}: {
    fixups: NonNullable<PlanningPhase["diversityFixups"]>;
}) {
    const beforeByN = new Map(fixups.before.map((b) => [b.n, b]));
    return (
        <div className="mb-3">
            <div className="text-xs font-semibold text-gray-600 mb-1">
                diversity fixups ({fixups.after.length})
            </div>
            <div className="border border-gray-200 rounded overflow-hidden">
                <table className="w-full text-xs font-mono">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="text-left px-2 py-1">slide</th>
                            <th className="text-left px-2 py-1">archetype</th>
                            <th className="text-left px-2 py-1">cardLayout</th>
                            <th className="text-left px-2 py-1">hasImage</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fixups.after.map((a) => {
                            const b = beforeByN.get(a.n);
                            const diff = (before: unknown, after: unknown) =>
                                before !== after ? (
                                    <span>
                                        <span className="text-gray-400 line-through">
                                            {String(before ?? "-")}
                                        </span>
                                        <span className="mx-1 text-gray-400">→</span>
                                        <span className="text-amber-700 font-bold">
                                            {String(after ?? "-")}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="text-gray-500">
                                        {String(after ?? "-")}
                                    </span>
                                );
                            return (
                                <tr key={a.n} className="border-t border-gray-100">
                                    <td className="px-2 py-1">{a.n}</td>
                                    <td className="px-2 py-1">
                                        {diff(b?.archetype, a.archetype)}
                                    </td>
                                    <td className="px-2 py-1">
                                        {diff(b?.cardLayout, a.cardLayout)}
                                    </td>
                                    <td className="px-2 py-1">
                                        {diff(b?.hasImage, a.hasImage)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SlideCard({ slide }: { slide: SlidePhaseEntry }) {
    const [open, setOpen] = useState(false);
    const [heroOpen, setHeroOpen] = useState(false);
    const title =
        (slide.plan &&
            typeof slide.plan === "object" &&
            (slide.plan.title || slide.plan.heading)) ||
        "(untitled)";
    const hasError = !!slide.error;
    const retried = (slide.retryCount || 0) > 0;

    return (
        <div
            className={`border rounded-lg bg-white ${
                hasError
                    ? "border-red-300"
                    : retried
                      ? "border-amber-300"
                      : "border-gray-200"
            }`}
        >
            <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setOpen(!open)}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {open ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm font-bold text-gray-700 w-8">
                        #{slide.slideNumber}
                    </span>
                    <span className="text-sm text-gray-800 truncate">
                        {String(title)}
                    </span>
                    {hasError && <Badge className="bg-red-100 text-red-800">error</Badge>}
                    {retried && !hasError && (
                        <Badge className="bg-amber-100 text-amber-800">
                            retried × {slide.retryCount}
                        </Badge>
                    )}
                    {slide.imageFallbackUsed && (
                        <Badge className="bg-amber-100 text-amber-800">
                            image fallback
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-3 text-xs font-mono text-gray-600">
                    <DurationBreakdown slide={slide} />
                    <span>{formatTokens(slide.tokens)}</span>
                    <span>{formatCost(slide.cost)}</span>
                </div>
            </div>
            {open && (
                <div className="border-t border-gray-200 p-4 space-y-4">
                    {slide.error && (
                        <div className="text-xs font-mono bg-red-50 border border-red-200 text-red-700 rounded p-2">
                            {slide.error}
                        </div>
                    )}

                    {(slide.imageUrl || slide.imagePrompt || slide.imageModel) && (
                        <div>
                            <div className="text-xs font-semibold text-gray-600 mb-1">
                                image
                            </div>
                            <div className="flex gap-3 items-start flex-wrap">
                                {slide.imageUrl && (
                                    <img
                                        src={slide.imageUrl}
                                        alt={`slide ${slide.slideNumber}`}
                                        className="max-h-60 object-contain rounded border border-gray-200 bg-gray-50"
                                    />
                                )}
                                <div className="flex-1 min-w-[200px] space-y-1.5">
                                    {slide.imageModel && (
                                        <div>
                                            <Badge className="bg-gray-100 text-gray-700 text-xs font-mono">
                                                {slide.imageModel}
                                            </Badge>
                                        </div>
                                    )}
                                    {slide.imagePrompt && (
                                        <TextBlock
                                            text={slide.imagePrompt}
                                            maxHeight="max-h-32"
                                        />
                                    )}
                                    {slide.imageUrl && (
                                        <div className="text-[10px] font-mono text-gray-400 break-all">
                                            {slide.imageUrl}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {slide.plan != null && (
                        <Labeled title="plan">
                            <JsonBlock data={slide.plan} maxHeight="max-h-60" />
                        </Labeled>
                    )}

                    {slide.contentPrompt && (
                        <Labeled title="contentPrompt">
                            <TextBlock text={slide.contentPrompt} />
                        </Labeled>
                    )}

                    {slide.toolCalls && slide.toolCalls.length > 0 && (
                        <Labeled title={`toolCalls (${slide.toolCalls.length})`}>
                            <div className="space-y-2">
                                {slide.toolCalls.map((tc, i) => (
                                    <div key={i}>
                                        <div className="text-xs font-mono font-bold text-gray-700">
                                            {tc.name}
                                        </div>
                                        <JsonBlock data={tc.input} maxHeight="max-h-40" />
                                    </div>
                                ))}
                            </div>
                        </Labeled>
                    )}

                    {slide.finalComponents != null && (
                        <Labeled title="finalComponents">
                            <JsonBlock data={slide.finalComponents} maxHeight="max-h-60" />
                        </Labeled>
                    )}

                    {(slide.heroHtmlPrompt || slide.heroHtmlResponse) && (
                        <div>
                            <div
                                className="text-xs font-semibold text-gray-600 mb-1 cursor-pointer inline-flex items-center gap-1"
                                onClick={() => setHeroOpen(!heroOpen)}
                            >
                                {heroOpen ? (
                                    <ChevronDown className="h-3 w-3" />
                                ) : (
                                    <ChevronRight className="h-3 w-3" />
                                )}
                                hero html
                            </div>
                            {slide.heroHtmlResponse && (
                                <iframe
                                    sandbox=""
                                    srcDoc={slide.heroHtmlResponse}
                                    className="w-full h-80 border border-gray-200 rounded bg-white"
                                    title={`hero-${slide.slideNumber}`}
                                />
                            )}
                            {heroOpen && (
                                <div className="space-y-2 mt-2">
                                    {slide.heroHtmlPrompt && (
                                        <Labeled title="heroHtmlPrompt">
                                            <TextBlock text={slide.heroHtmlPrompt} />
                                        </Labeled>
                                    )}
                                    {slide.heroHtmlResponse && (
                                        <Labeled title="heroHtmlResponse (raw)">
                                            <TextBlock
                                                text={slide.heroHtmlResponse}
                                                maxHeight="max-h-80"
                                            />
                                        </Labeled>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
